// Pre-existing native import - exempt from multi-version OTA adapter layer requirement.
// New usages should be migrated to the adapter layer.
// eslint-disable-next-line no-restricted-imports
import { Share } from "@capacitor/share";
import { isDefined } from "@clipboard-health/util-ts";
import { APP_V2_APP_EVENTS, isCapacitorPlatform, useToast } from "@src/appV2/lib";
import { logError, logEvent } from "@src/appV2/lib/analytics";
import { useMaxReferralBonusAmount } from "@src/appV2/Referral/Bonus/flags";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";
import { useCallback } from "react";

import { type PlacementDetail } from "../../api/useGetPlacement";
import { formatPayRange } from "../../utils";
import { getPlacementHiringLink } from "../../utils/getPlacementHiringLink";

interface HandleShareReferralParameters {
  placementId: string;
  payRange: PlacementDetail["payRange"];
  workplaceName: string;
  qualificationDisplayLabel: string;
  formattedAddress: string;
  additionalContext?: Record<string, unknown>;
}

export function useSharePlacementReferral() {
  const { showSuccessToast, showErrorToast } = useToast();
  const worker = useDefinedWorker();
  const { referralCode } = worker;

  const maxReferralBonusAmount = useMaxReferralBonusAmount("postOnboardingBonusInDollars");

  const handleShareReferral = useCallback(
    async (params: HandleShareReferralParameters) => {
      const {
        payRange,
        workplaceName,
        qualificationDisplayLabel,
        formattedAddress,
        placementId,
        additionalContext = {},
      } = params;

      const eventContext = {
        ...additionalContext,
        placementId,
        workerId: worker.userId,
        referralCode,
      };

      logEvent(APP_V2_APP_EVENTS.PLACEMENT_REFER_JOB_CLICKED, eventContext);
      if (!isDefined(referralCode)) {
        logEvent(APP_V2_APP_EVENTS.PLACEMENT_REFER_JOB_SHARE_FAILURE, eventContext);
        showErrorToast("Failed to get referral link, please try again later.");
        return;
      }

      const referralLink = getPlacementHiringLink(placementId, referralCode);

      try {
        if (isCapacitorPlatform()) {
          await Share.share({
            title: `Job at ${workplaceName} - ${qualificationDisplayLabel} ${
              formatPayRange({
                payRange,
                showPerHour: true,
                withFractionDigits: true,
              }) ?? ""
            }. ${formattedAddress}`,
            text: `Thought you might like this job on Clipboard (and there's tons more, too) - sign up through the link and we'll both get $${maxReferralBonusAmount} when you get a Clipboard job or work your first Clipboard flex shift!\n\n${referralLink}`,
          });
        } else {
          await navigator.clipboard.writeText(referralLink);
          showSuccessToast("Link copied to clipboard");
        }

        logEvent(APP_V2_APP_EVENTS.PLACEMENT_REFER_JOB_SHARE_SUCCESS, eventContext);
      } catch (error) {
        logError(APP_V2_APP_EVENTS.PLACEMENT_REFER_JOB_SHARE_FAILURE, {
          metadata: eventContext,
          error,
        });
      }
    },
    [maxReferralBonusAmount, referralCode, showErrorToast, showSuccessToast, worker.userId]
  );

  return {
    handleShareReferral,
    maxReferralBonusAmount,
  };
}
